import axios from "axios";

import {
  BrandMessage,
  BrandMessageInput,
  BusinessFeature,
  BusinessFeatureInput,
  CompanyProfile,
  CompanyProfileInput,
  CompetitorProfile,
  CompetitorProfileInput,
  ContentLibraryItem,
  ContentLibraryItemInput,
  CustomerProfileSubset,
  CustomerProfileSubsetInput,
  IdealCustomerProfile,
  IdealCustomerProfileInput,
  MessagingPillar,
  MessagingPillarInput,
  SocialProof,
  SocialProofInput,
} from "../types/company-knowledge";

// Company profile [singleton]

export async function getCompanyProfile(
  workspaceSlug: string,
): Promise<CompanyProfile> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/company-profile`,
  );
  return rsp.data;
}

export async function updateCompanyProfile(
  workspaceSlug: string,
  data: CompanyProfileInput,
): Promise<CompanyProfile> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/company-profile`,
    data,
  );
  return rsp.data;
}

// Ideal customer profile [singleton]

export async function getIdealCustomerProfile(
  workspaceSlug: string,
): Promise<IdealCustomerProfile> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/ideal-customer-profile`,
  );
  return rsp.data;
}

export async function updateIdealCustomerProfile(
  workspaceSlug: string,
  data: IdealCustomerProfileInput,
): Promise<IdealCustomerProfile> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/ideal-customer-profile`,
    data,
  );
  return rsp.data;
}

// Brand message [singleton]

export async function getBrandMessage(
  workspaceSlug: string,
): Promise<BrandMessage> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/brand-message`,
  );
  return rsp.data;
}

export async function updateBrandMessage(
  workspaceSlug: string,
  data: BrandMessageInput,
): Promise<BrandMessage> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/brand-message`,
    data,
  );
  return rsp.data;
}

// Customer profile subsets [repeatable]

export async function getCustomerProfileSubsets(
  workspaceSlug: string,
): Promise<CustomerProfileSubset[]> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/customer-profile-subsets`,
  );
  return rsp.data;
}

export async function createCustomerProfileSubset(
  workspaceSlug: string,
  data: CustomerProfileSubsetInput,
): Promise<CustomerProfileSubset> {
  const rsp = await axios.post(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/customer-profile-subsets`,
    data,
  );
  return rsp.data;
}

export async function updateCustomerProfileSubset(
  workspaceSlug: string,
  id: number,
  data: CustomerProfileSubsetInput,
): Promise<CustomerProfileSubset> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/customer-profile-subsets/${id}`,
    data,
  );
  return rsp.data;
}

export async function deleteCustomerProfileSubset(
  workspaceSlug: string,
  id: number,
): Promise<void> {
  await axios.delete(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/customer-profile-subsets/${id}`,
  );
  return undefined;
}

// Competitor profiles [repeatable]

export async function getCompetitorProfiles(
  workspaceSlug: string,
): Promise<CompetitorProfile[]> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/competitor-profiles`,
  );
  return rsp.data;
}

export async function createCompetitorProfile(
  workspaceSlug: string,
  data: CompetitorProfileInput,
): Promise<CompetitorProfile> {
  const rsp = await axios.post(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/competitor-profiles`,
    data,
  );
  return rsp.data;
}

export async function updateCompetitorProfile(
  workspaceSlug: string,
  id: number,
  data: CompetitorProfileInput,
): Promise<CompetitorProfile> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/competitor-profiles/${id}`,
    data,
  );
  return rsp.data;
}

export async function deleteCompetitorProfile(
  workspaceSlug: string,
  id: number,
): Promise<void> {
  await axios.delete(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/competitor-profiles/${id}`,
  );
  return undefined;
}

// Messaging pillars [repeatable]

export async function getMessagingPillars(
  workspaceSlug: string,
): Promise<MessagingPillar[]> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/messaging-pillars`,
  );
  return rsp.data;
}

export async function createMessagingPillar(
  workspaceSlug: string,
  data: MessagingPillarInput,
): Promise<MessagingPillar> {
  const rsp = await axios.post(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/messaging-pillars`,
    data,
  );
  return rsp.data;
}

export async function updateMessagingPillar(
  workspaceSlug: string,
  id: number,
  data: MessagingPillarInput,
): Promise<MessagingPillar> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/messaging-pillars/${id}`,
    data,
  );
  return rsp.data;
}

export async function deleteMessagingPillar(
  workspaceSlug: string,
  id: number,
): Promise<void> {
  await axios.delete(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/messaging-pillars/${id}`,
  );
  return undefined;
}

// Business features [repeatable]

export async function getBusinessFeatures(
  workspaceSlug: string,
): Promise<BusinessFeature[]> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/business-features`,
  );
  return rsp.data;
}

export async function createBusinessFeature(
  workspaceSlug: string,
  data: BusinessFeatureInput,
): Promise<BusinessFeature> {
  const rsp = await axios.post(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/business-features`,
    data,
  );
  return rsp.data;
}

export async function updateBusinessFeature(
  workspaceSlug: string,
  id: number,
  data: BusinessFeatureInput,
): Promise<BusinessFeature> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/business-features/${id}`,
    data,
  );
  return rsp.data;
}

export async function deleteBusinessFeature(
  workspaceSlug: string,
  id: number,
): Promise<void> {
  await axios.delete(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/business-features/${id}`,
  );
  return undefined;
}

// Content library [repeatable]

export async function getContentLibrary(
  workspaceSlug: string,
): Promise<ContentLibraryItem[]> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/content-library`,
  );
  return rsp.data;
}

export async function createContentLibraryItem(
  workspaceSlug: string,
  data: ContentLibraryItemInput,
): Promise<ContentLibraryItem> {
  const rsp = await axios.post(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/content-library`,
    data,
  );
  return rsp.data;
}

export async function updateContentLibraryItem(
  workspaceSlug: string,
  id: number,
  data: ContentLibraryItemInput,
): Promise<ContentLibraryItem> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/content-library/${id}`,
    data,
  );
  return rsp.data;
}

export async function deleteContentLibraryItem(
  workspaceSlug: string,
  id: number,
): Promise<void> {
  await axios.delete(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/content-library/${id}`,
  );
  return undefined;
}

// Social proofs [repeatable]

export async function getSocialProofs(
  workspaceSlug: string,
): Promise<SocialProof[]> {
  const rsp = await axios.get(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/social-proofs`,
  );
  return rsp.data;
}

export async function createSocialProof(
  workspaceSlug: string,
  data: SocialProofInput,
): Promise<SocialProof> {
  const rsp = await axios.post(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/social-proofs`,
    data,
  );
  return rsp.data;
}

export async function updateSocialProof(
  workspaceSlug: string,
  id: number,
  data: SocialProofInput,
): Promise<SocialProof> {
  const rsp = await axios.put(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/social-proofs/${id}`,
    data,
  );
  return rsp.data;
}

export async function deleteSocialProof(
  workspaceSlug: string,
  id: number,
): Promise<void> {
  await axios.delete(
    `/api/v1/workspaces/${workspaceSlug}/company-knowledge/social-proofs/${id}`,
  );
  return undefined;
}
